import { useEffect, useState } from 'react'
import { usePollen, HOTSPOT_NICHT_MEHR_ERKANNT, HOTSPOT_EINORDNUNGEN, hotspotEinordnungFarbe, hotspotEinordnungLabel } from '@pilot-hub/ui'
import { CircleMarker, Marker, Popup, Tooltip } from 'react-leaflet'
import L from 'leaflet'
import { api } from '@pilot-hub/api-client'
import { PanoramaLayer } from '@pilot-hub/ui/components/karte/index'


// Ebenen „Lagebild (Fotos)“ und „Hotspots“: Fotos aus DJI Pilot 2 (Media-Modul, Medien-Worker) mit Blickrichtung und
// Hotspots aus radiometrischen IR-Fotos (DJI Thermal SDK). Bilder werden nur mit Anmeldung geliefert (Blob-Abruf mit Token).
// Weitwinkel: verkleinerte Fassung 2560 px + Vorschau 400 px, Original nur bis zum Ablauf der Aufbewahrung.

export interface Foto {
  id: number
  name: string
  typ: 'weit' | 'zoom' | 'ir' | 'pano'
  aufnahme_zeit: string | null
  lat: number | null
  lon: number | null
  hoehe_rel_m: number | null
  gimbal_gier: number | null
  gimbal_nick: number | null
  temp_min: number | null
  temp_max: number | null
  ir_hinweis: string | null
  hat_vorschau: boolean
  hat_verkleinert: boolean
  hat_original: boolean
  dateien_geloescht: boolean
  /** Panoramen (typ 'pano'): Art, Bildwinkel und vorhandene Fassungen (8192/4096 px) */
  panorama_art?: 'kugel' | 'breit' | 'vertikal' | null
  pano_fov_h?: number | null
  pano_fov_v?: number | null
  hat_pano_gross?: boolean
  hat_pano_klein?: boolean
  /** Für den Einsatzbericht markiert (Führung/Admin) */
  bericht_markiert?: boolean
  bericht_markiert_am?: string | null
  bericht_markiert_von_name?: string | null
}

export interface Hotspot {
  id: number
  medium_id: number
  temp_max: number
  temp_mittel: number | null
  pixel_anzahl: number | null
  lat: number
  lon: number
  aufnahme_zeit: string | null
  name: string
  hat_vorschau: boolean
  /** Einordnung durch die Führung: RELEVANT, HINWEIS, WILDTIER, FEHLALARM; leer = nicht eingeordnet */
  einordnung?: string | null
  einordnung_am?: string | null
  einordnung_von_name?: string | null
  /** Eingeordneter Hotspot, der nach einer Neuberechnung des Fotos nicht mehr entsteht (bleibt erhalten) */
  nicht_mehr_erkannt?: boolean
  nicht_mehr_erkannt_am?: string | null
}

export interface MedienStatus {
  belegt_bytes?: number
  grenze_bytes?: number
  voll?: boolean
  warnungen?: Array<{ code: string; text: string }>
}

export type FotoFilter = 'alle' | 'weit' | 'ir' | 'pano'

/** Fotos (verarbeitet) des Einsatzes, alle 15 s neu */
export function useFotos(einsatzId: string | number | null | undefined, aktiv: boolean) {
  const [liste, setListe] = useState<Foto[]>([])
  useEffect(() => { setListe([]) }, [einsatzId, aktiv])
  usePollen('Fotos', lebt => api.get(`/dji/medien/liste/${einsatzId}`).then(r => { if (lebt()) setListe(r.data as Foto[]) }),
    15000, [einsatzId], !!einsatzId && aktiv)
  return liste
}

export function useHotspots(einsatzId: string | number | null | undefined, aktiv: boolean) {
  const [liste, setListe] = useState<Hotspot[]>([])
  useEffect(() => { setListe([]) }, [einsatzId, aktiv])
  usePollen('Hotspots', lebt => api.get(`/dji/medien/hotspots/${einsatzId}`).then(r => { if (lebt()) setListe(r.data as Hotspot[]) }),
    15000, [einsatzId], !!einsatzId && aktiv)
  return liste
}

/** Speicherstand und Warnungen (belegt/Obergrenze) */
export function useMedienStatus(aktiv: boolean) {
  const [status, setStatus] = useState<MedienStatus | null>(null)
  usePollen('Medien-Speicherstand', lebt => api.get('/dji/medien/status').then(r => { if (lebt()) setStatus(r.data as MedienStatus) }),
    60000, [], aktiv)
  return status
}

const gb = (b?: number) => ((b ?? 0) / 1024 ** 3).toLocaleString('de-DE', { maximumFractionDigits: 1 })

export function MedienStatusHinweis({ status }: { status: MedienStatus | null }) {
  if (!status || status.belegt_bytes == null) return null
  const warn = status.warnungen ?? []
  return (
    <div className="px-2 pb-1 text-xs text-dark-text-subtle">
      <div>Medienspeicher: {gb(status.belegt_bytes)} von {gb(status.grenze_bytes)} GB belegt</div>
      {warn.map(w => (
        <div key={w.code} className={`mt-1 rounded border px-2 py-1 ${status.voll || w.code === 'voll' ? 'border-red-500/60 bg-red-500/10 text-red-300' : 'border-amber-500/60 bg-amber-500/10 text-amber-300'}`}>
          {w.text}
        </div>
      ))}
    </div>
  )
}

/** Bild mit Anmeldung laden (img-Tags können keinen Token senden) */
function AuthBild({ pfad, alt }: { pfad: string; alt: string }) {
  const [url, setUrl] = useState<string | null>(null)
  const [fehler, setFehler] = useState(false)
  useEffect(() => {
    let lebt = true
    let objekt: string | null = null
    api.get(pfad, { responseType: 'blob' })
      .then(r => { objekt = URL.createObjectURL(r.data); if (lebt) setUrl(objekt) })
      .catch(() => { if (lebt) setFehler(true) })
    return () => { lebt = false; if (objekt) URL.revokeObjectURL(objekt) }
  }, [pfad])
  if (fehler) return <div className="text-xs text-red-400">Bild nicht verfügbar</div>
  if (!url) return <div className="h-24 w-[240px] animate-pulse rounded bg-gray-300" />
  return <img src={url} alt={alt} className="block max-h-[200px] w-[240px] rounded object-contain" />
}

/** Bild in neuem Tab öffnen (Abruf mit Token, dann Blob-Adresse) */
function oeffnen(pfad: string) {
  const w = window.open('', '_blank')
  api.get(pfad, { responseType: 'blob' })
    .then(r => { const u = URL.createObjectURL(r.data); if (w) w.location.href = u; else window.open(u, '_blank') })
    .catch(() => { if (w) w.close() })
}

const zeitText = (z: string | null) => z ? new Date(z).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '–'

function fotoIcon(f: Foto) {
  const farbe = f.typ === 'ir' ? '#f97316' : '#2563eb'
  const gier = f.gimbal_gier ?? 0
  // Punkt mit Strahl in Blickrichtung (Gimbal-Richtung ab Nord, im Uhrzeigersinn)
  const strahl = f.gimbal_gier == null ? '' :
    `<div style="position:absolute;left:13px;top:13px;width:0;height:0;transform:rotate(${gier}deg);transform-origin:0 0">` +
    `<div style="position:absolute;left:-2px;top:-26px;width:4px;height:22px;background:${farbe};opacity:.85;border-radius:2px"></div>` +
    `<div style="position:absolute;left:-6px;top:-32px;border:6px solid transparent;border-bottom:9px solid ${farbe};border-top:0"></div></div>`
  return L.divIcon({
    html: `<div style="position:relative;width:26px;height:26px">${strahl}<div style="position:absolute;left:6px;top:6px;width:14px;height:14px;border-radius:9999px;background:${farbe};border:2px solid #fff;box-shadow:0 0 3px rgba(0,0,0,.6)"></div>` +
      (f.bericht_markiert ? `<div title="Für den Bericht markiert" style="position:absolute;left:1px;top:1px;width:24px;height:24px;border-radius:9999px;border:3px solid #22c55e;box-sizing:border-box"></div>` : '') + `</div>`,
    className: '', iconSize: [26, 26], iconAnchor: [13, 13],
  })
}

/** Kartenebene (innerhalb von MapContainer): Fotopunkte mit Blickrichtung, Klick zeigt die Vorschau */
export function FotoLayer({ fotos: fotosRoh, filter }: { fotos: Foto[]; filter: FotoFilter }) {
  // Änderungen (Bericht-Markierung) sofort anzeigen; die nächste Abfrage (alle 15 s) bringt denselben Stand vom Server
  const [lokal, setLokal] = useState<Record<number, Partial<Foto>>>({})
  useEffect(() => { setLokal({}) }, [fotosRoh])
  const fotos = fotosRoh.map(f => lokal[f.id] ? { ...f, ...lokal[f.id] } : f)
  return (
    <>
      {/* Panoramen haben ein eigenes Symbol; Klick öffnet den Panorama-Viewer */}
      {(filter === 'alle' || filter === 'pano') && <PanoramaLayer panoramen={fotos.filter(f => f.typ === 'pano')} />}
      {fotos.filter(f => f.lat != null && f.lon != null && f.typ !== 'pano' && (filter === 'alle' || (filter === 'ir' ? f.typ === 'ir' : filter === 'weit')))
        .map(f => (
        <Marker key={f.id} position={[f.lat!, f.lon!]} icon={fotoIcon(f)}>
          <Tooltip direction="top" offset={[0, -14]}>{f.typ === 'ir' ? 'IR' : f.typ === 'zoom' ? 'Zoom' : 'Weitwinkel'} · {zeitText(f.aufnahme_zeit)}</Tooltip>
          <Popup minWidth={250}>
            <div className="space-y-1 text-xs text-gray-800">
              {f.hat_vorschau ? <AuthBild pfad={`/dji/medien/datei/${f.id}/vorschau`} alt={f.name} />
                : <div className="rounded bg-gray-200 p-2">Foto nicht mehr vorhanden</div>}
              <div className="font-semibold">{f.typ === 'ir' ? 'Wärmebild (IR)' : f.typ === 'zoom' ? 'Zoom-Foto' : 'Weitwinkel-Foto'} · {zeitText(f.aufnahme_zeit)}</div>
              <div>
                {f.hoehe_rel_m != null && <>Höhe {Math.round(f.hoehe_rel_m)} m</>}
                {f.gimbal_gier != null && <> · Blick {Math.round(((f.gimbal_gier % 360) + 360) % 360)}°</>}
                {f.gimbal_nick != null && <> · Neigung {Math.round(f.gimbal_nick)}°</>}
              </div>
              {f.typ === 'ir' && f.temp_max != null && (
                <div>Temperatur {f.temp_min?.toFixed(1)} bis {f.temp_max.toFixed(1)} °C</div>
              )}
              {f.typ === 'ir' && f.temp_max == null && f.ir_hinweis && <div className="text-amber-700">{f.ir_hinweis}</div>}
              <BerichtMarkierung foto={f} onGeaendert={p => setLokal(l => ({ ...l, [f.id]: p }))} />
              <div className="flex gap-3 pt-1">
                {f.hat_verkleinert && <button type="button" className="font-semibold text-blue-700 underline" onClick={() => oeffnen(`/dji/medien/datei/${f.id}/verkleinert`)}>
                  Öffnen (2560 px)</button>}
                {f.hat_original && <button type="button" className="font-semibold text-blue-700 underline" onClick={() => oeffnen(`/dji/medien/datei/${f.id}/original`)}>
                  Original</button>}
              </div>
            </div>
          </Popup>
        </Marker>
      ))}
    </>
  )
}

const hotspotFarbe = (t: number) => t >= 80 ? '#dc2626' : t >= 50 ? '#f97316' : t >= 35 ? '#facc15' : '#fde68a'

/** Kartenebene (innerhalb von MapContainer): Hotspots aus IR-Fotos mit Temperatur, Klick zeigt die IR-Vorschau */
export function HotspotLayer({ hotspots: hotspotsRoh }: { hotspots: Hotspot[] }) {
  const [lokal, setLokal] = useState<Record<number, Partial<Hotspot>>>({})
  useEffect(() => { setLokal({}) }, [hotspotsRoh])
  const hotspots = hotspotsRoh.map(h => lokal[h.id] ? { ...h, ...lokal[h.id] } : h)
  return (
    <>
      {hotspots.map(h => (
        // Füllung = Temperatur wie bisher; ein eingeordneter Hotspot bekommt zusätzlich einen farbigen Rand und den Text der Einordnung
        <CircleMarker key={h.id} center={[h.lat, h.lon]} radius={9}
          pathOptions={{ color: hotspotEinordnungFarbe(h.einordnung) ?? '#fff', weight: h.einordnung ? 4 : 2, fillColor: hotspotFarbe(h.temp_max), fillOpacity: 0.9 }}>
          <Tooltip direction="top" offset={[0, -8]} permanent={false}>{h.temp_max.toFixed(1)} °C{h.einordnung ? ` · ${hotspotEinordnungLabel(h.einordnung)}` : ''}{h.nicht_mehr_erkannt ? ' · nicht mehr erkannt' : ''}</Tooltip>
          <Popup minWidth={250}>
            <div className="space-y-1 text-xs text-gray-800">
              {h.hat_vorschau ? <AuthBild pfad={`/dji/medien/datei/${h.medium_id}/vorschau`} alt={h.name} />
                : <div className="rounded bg-gray-200 p-2">Foto nicht mehr vorhanden</div>}
              <div className="font-semibold">Hotspot {h.temp_max.toFixed(1)} °C{h.temp_mittel != null && <> (Ø {h.temp_mittel.toFixed(1)} °C)</>}</div>
              <div>{zeitText(h.aufnahme_zeit)}{h.pixel_anzahl != null && <> · {h.pixel_anzahl} Bildpunkte</>}</div>
              <HotspotEinordnung hotspot={h} onGeaendert={p => setLokal(l => ({ ...l, [h.id]: p }))} />
            </div>
          </Popup>
        </CircleMarker>
      ))}
    </>
  )
}

const fehlerDetail = (e: any, fallback: string) => { const d = e?.response?.data?.detail; return typeof d === 'string' ? d : fallback }

/** „Für den Einsatzbericht markieren“ direkt am Foto (nur Führung/Admin; das Backend prüft die Rolle) */
function BerichtMarkierung({ foto, onGeaendert }: { foto: Foto; onGeaendert: (p: Partial<Foto>) => void }) {
  const [busy, setBusy] = useState(false)
  const [fehler, setFehler] = useState('')
  const umschalten = async () => {
    setBusy(true); setFehler('')
    try {
      const r = await api.put(`/dji/medien/galerie/foto/${foto.id}/bericht`, { markiert: !foto.bericht_markiert })
      onGeaendert({ bericht_markiert: !!r.data.bericht_markiert, bericht_markiert_am: r.data.bericht_markiert_am ?? null, bericht_markiert_von_name: r.data.bericht_markiert_von_name ?? null })
    } catch (e) { setFehler(fehlerDetail(e, 'Die Markierung konnte nicht gespeichert werden.')) }
    finally { setBusy(false) }
  }
  return (
    <div className="space-y-1 border-t border-gray-300 pt-2" data-testid="lagebild-bericht">
      {foto.bericht_markiert && <div className="font-semibold text-green-700" data-testid="lagebild-bericht-status">Für den Bericht markiert{foto.bericht_markiert_von_name ? ` (${foto.bericht_markiert_von_name}` : ''}{foto.bericht_markiert_von_name && foto.bericht_markiert_am ? `, ${zeitText(foto.bericht_markiert_am)}` : ''}{foto.bericht_markiert_von_name ? ')' : ''}</div>}
      <button type="button" disabled={busy} onClick={umschalten} aria-pressed={!!foto.bericht_markiert} data-testid="lagebild-bericht-umschalten"
        className={`min-h-[44px] w-full rounded-lg border px-3 text-sm font-semibold disabled:opacity-50 ${foto.bericht_markiert ? 'border-green-600 bg-green-100 text-green-900' : 'border-gray-400 bg-white text-gray-900'}`}>
        {foto.bericht_markiert ? 'Markierung entfernen' : 'Für den Bericht markieren'}</button>
      {fehler && <div className="text-red-700" role="alert">{fehler}</div>}
    </div>
  )
}

/** Hotspot einordnen (Relevant, Hinweis, Wildtier, Fehlalarm); erneutes Tippen hebt die Einordnung auf = „nicht eingeordnet“ */
function HotspotEinordnung({ hotspot, onGeaendert }: { hotspot: Hotspot; onGeaendert: (p: Partial<Hotspot>) => void }) {
  const [busy, setBusy] = useState(false)
  const [fehler, setFehler] = useState('')
  const setzen = async (neu: string | null) => {
    setBusy(true); setFehler('')
    try {
      const r = await api.put(`/dji/medien/galerie/hotspot/${hotspot.id}/einordnung`, { einordnung: neu })
      onGeaendert({ einordnung: r.data.einordnung ?? null, einordnung_am: r.data.einordnung_am ?? null, einordnung_von_name: null })
    } catch (e) { setFehler(fehlerDetail(e, 'Die Einordnung konnte nicht gespeichert werden.')) }
    finally { setBusy(false) }
  }
  return (
    <div className="space-y-1 border-t border-gray-300 pt-2" data-testid="hotspot-einordnung">
      <div className="font-semibold" data-testid="hotspot-einordnung-text" style={{ color: hotspotEinordnungFarbe(hotspot.einordnung) ?? undefined }}>
        Einordnung: {hotspotEinordnungLabel(hotspot.einordnung)}</div>
      {hotspot.nicht_mehr_erkannt && <div className="font-semibold text-amber-700" data-testid="hotspot-nicht-mehr-erkannt">{HOTSPOT_NICHT_MEHR_ERKANNT}</div>}
      {hotspot.einordnung && hotspot.einordnung_von_name && <div>{hotspot.einordnung_von_name}{hotspot.einordnung_am ? `, ${zeitText(hotspot.einordnung_am)}` : ''}</div>}
      <div className="grid grid-cols-2 gap-1">
        {HOTSPOT_EINORDNUNGEN.map(x => (
          <button key={x.key} type="button" disabled={busy} aria-pressed={hotspot.einordnung === x.key} data-testid={`einordnung-${x.key}`}
            onClick={() => setzen(hotspot.einordnung === x.key ? null : x.key)}
            className={`min-h-[44px] rounded-lg border px-2 text-sm font-semibold disabled:opacity-50 ${hotspot.einordnung === x.key ? 'border-gray-900 bg-gray-200 text-gray-900' : 'border-gray-400 bg-white text-gray-900'}`}>{x.label}</button>
        ))}
      </div>
      {fehler && <div className="text-red-700" role="alert">{fehler}</div>}
    </div>
  )
}
